import { useSnackbar } from 'notistack'
import {
  createModule as createModuleService,
  updateModule as updateModuleService,
  deleteModule as deleteModuleService,
  getAnalyticsCurriculumCurrent as getAnalyticsCurriculumCurrentService,
  generateDataForAnalyticsCurriculum as generateDataForAnalyticsCurriculumService,
  getAnalyticsCurriculumByField as getAnalyticsCurriculumByFieldService,
  getAnalyticsCurriculumCurrentProgress as getAnalyticsCurriculumCurrentProgressService,
  getHistory as getHistoryService,
  getAnalyticsCurriculumByFieldHistory as getAnalyticsCurriculumByFieldHistoryService,
  getClassesHistory as getClassesHistoryService,
  getStudentsHistory as getStudentsHistoryService,
  deleteHistory as deleteHistoryService
} from '../../services/analytics/analytics-curriculum.service'
import { updateDevelopmentalRubricBatch as updateDevelopmentalRubricBatchService } from '../../services/developmental-rubrics/developmental-rubrics.service'
import { validate } from '../../utils'
import { handlerErrorV2 } from '../../utils/errors'
import isEmpty from 'lodash/isEmpty'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  toggleLoading,
  updateDatasets,
  updateFieldValue,
  updateDatasetsFieldValue
} from '../../store/actions/analytics.actions'
import { useCallback, useState } from 'react'
import { updateDevelopmentalRubricsListMethod } from '../../store/actions/developmental-rubrics.actions'
import { assign } from 'lodash'
import { getClassesSkipLimit as getClassesSkipLimitService } from '../../services/classes/classes.service'
import { getUsersByRoleIdSkipLimit as getUsersByRoleIdSkipLimitService } from '../../services/user/user.service'

const useAnalyticsCurriculum = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const { code } = useParams()

  const getAnalyticsCurriculumCurrent = useCallback(async () => {
    dispatch(updateDatasets('analyticsCurriculum', []))
    dispatch(toggleLoading('analyticsCurriculum', true))
    try {
      const response = await getAnalyticsCurriculumCurrentService(code)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateDatasets('analyticsCurriculum', response.data))
        return { error: false, data: response.data }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(toggleLoading('analyticsCurriculum', false))
    }
  }, [code, dispatch])

  const generateDataForAnalyticsCurriculum = useCallback(
    async (data) => {
      dispatch(updateFieldValue('analyticsCurriculum', 'isLoading', true))
      try {
        const response = await generateDataForAnalyticsCurriculumService(code, data)
        if (!response.data && response.error) {
          return { error: true, message: response.error.message || 'An error ocurred' }
        } else {
          dispatch(updateFieldValue('analyticsCurriculum', 'data', response.data))
          dispatch(updateDatasetsFieldValue('analyticsCurriculum', 'data', response.data))
          dispatch(updateFieldValue('analyticsCurriculum', 'progress', response.data?.progress))

          return { error: false, data: response.data }
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        return { error: true, message }
      } finally {
        dispatch(updateFieldValue('analyticsCurriculum', 'isLoading', false))
      }
    },
    [code]
  )
  const getProgress = useCallback(async () => {
    dispatch(updateFieldValue('analyticsCurriculum', 'isLoadingProgress', true))
    try {
      const response = await getAnalyticsCurriculumCurrentProgressService(code)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateFieldValue('analyticsCurriculum', 'progress', response.data?.progress))
        return { error: false, data: response.data }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(updateFieldValue('analyticsCurriculum', 'isLoadingProgress', false))
    }
  }, [code])

  const getAnalyticsCurriculumByStudent = async (id, data, view = 'current', historyId) => {
    let loading = view === 'current' ? 'byLoading' : 'byLoadingHistory'
    try {
      let dataField = view === 'current' ? 'byStudent' : 'byStudentHistory'
      dispatch(updateFieldValue('analyticsCurriculum', loading, true))
      let response = { data: null, error: 'Error: view invalid' }
      if (view === 'current') {
        response = await getAnalyticsCurriculumByFieldService(code, 'student', id, data, view)
      }
      if (view === 'history') {
        response = await getAnalyticsCurriculumByFieldHistoryService(code, historyId, 'student', id, data)
      }
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateFieldValue('analyticsCurriculum', dataField, response.data))

        return { error: false, data: response.data }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(updateFieldValue('analyticsCurriculum', loading, false))
    }
  }

  const getAnalyticsCurriculumByClasses = async (id, data, view = 'current', historyId) => {
    let loading = view === 'current' ? 'byLoading' : 'byLoadingHistory'
    try {
      dispatch(updateFieldValue('analyticsCurriculum', loading, true))

      let response = { data: null, error: 'Error: view invalid' }
      if (view === 'current') {
        response = await getAnalyticsCurriculumByFieldService(code, 'class', id, data, view)
      }
      if (view === 'history') {
        response = await getAnalyticsCurriculumByFieldHistoryService(code, historyId, 'class', id, data)
      }
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        let dataField = view === 'current' ? 'byClasses' : 'byClassesHistory'
        dispatch(updateFieldValue('analyticsCurriculum', dataField, response.data))
        return { error: false, data: response.data }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(updateFieldValue('analyticsCurriculum', loading, false))
    }
  }

  const validateForm = (module, form) => {
    const fieldRules = {
      code: 'required',
      name: 'required',
      color: 'required'
    }

    if (['status', 'results'].includes(module)) {
      fieldRules['bgColor'] = 'required'
      fieldRules['border'] = 'required'
    }

    if (['results'].includes(module)) {
      fieldRules['rules'] = 'required'
      fieldRules['priority'] = 'required'
      fieldRules['position'] = 'required'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const formatFormData = (module, form) => {
    const formData = assign({}, form)
    delete formData._id
    delete formData.createdAt
    delete formData.updatedAt
    delete formData.__v

    return formData
  }

  const updateStatusDevelopmentalRubricBatch = async (curriculumId, drIds, status) => {
    try {
      const response = await updateDevelopmentalRubricBatchService(curriculumId, 'status', { status, drIds })
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateDevelopmentalRubricsListMethod(curriculumId, 'BATCH_PUT', response.data))
        enqueueSnackbar(`Unit of learning status updated successfully`, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const createModule = async (module, form = {}) => {
    const result = validateForm(module, form)
    if (result.error) {
      return result
    }

    try {
      const formData = formatFormData(module, form)
      const response = await createModuleService(code, module, formData)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        enqueueSnackbar(`${module} created successfully`, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateModule = async (module, moduleId, form = {}) => {
    const result = validateForm(module, form)
    if (result.error) {
      return result
    }

    try {
      const formData = formatFormData(module, form)
      const response = await updateModuleService(code, module, moduleId, formData)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        enqueueSnackbar(`${module} updated successfully`, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteModule = async (module, moduleId) => {
    try {
      const response = await deleteModuleService(code, module, moduleId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        enqueueSnackbar(`${module} deleted successfully`, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const getHistory = async (data) => {
    dispatch(updateFieldValue('analyticsCurriculum', 'historyLoading', true))
    try {
      const response = await getHistoryService(code, data)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateFieldValue('analyticsCurriculum', 'historyList', response.data))
        return { error: false, data: response.data }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(updateFieldValue('analyticsCurriculum', 'historyLoading', false))
    }
  }

  const getClasses = useCallback(
    async (view, curriculumId, historyId, skip, limit, filter) => {
      dispatch(updateFieldValue('analyticsCurriculum', 'isLoadingList', true))
      try {
        let response = { data: null, error: 'Error: view invalid' }
        const filterData = {
          code: filter?.code,
          name: filter?.name,
          yearLevel: filter?.yearLevel,
          curriculumElements: filter?.curriculumElements,
          subjectId: filter?.subjectId,
          teachers: filter?.teachersId,
          studentId: filter?.studentId,
          ids: filter?.ids
        }
        if (view === 'history') {
          response = await getClassesHistoryService(code, historyId, skip, limit, filterData)
        }
        if (view === 'current') {
          response = await getClassesSkipLimitService(curriculumId, skip, limit, filterData)
        }
        if (!response.data && response.error) {
          return { error: true, message: response.error.message || 'An error ocurred' }
        } else {
          const data = {
            data: response.data?.data || [],
            count: Array.isArray(response.data?.count)
              ? response.data?.count.length || 0
              : response.data?.count || 0
          }
          dispatch(updateFieldValue('analyticsCurriculum', 'list', data))
          return { error: false, data }
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        return { error: true, message }
      } finally {
        dispatch(updateFieldValue('analyticsCurriculum', 'isLoadingList', false))
      }
    },
    [code, dispatch]
  )

  const getStudents = useCallback(
    async (view, historyId, skip, limit, filter) => {
      dispatch(updateFieldValue('analyticsCurriculum', 'isLoadingList', true))
      try {
        let response = { data: null, error: 'Error: view invalid' }

        if (view === 'history') {
          response = await getStudentsHistoryService(code, historyId, skip, limit, filter)
        }
        if (view === 'current') {
          response = await getUsersByRoleIdSkipLimitService('Student', skip, limit, filter)
        }
        if (!response.data && response.error) {
          return { error: true, message: response.error.message || 'An error ocurred' }
        } else {
          const data = {
            data: response.data?.users || [],
            count: Array.isArray(response.data?.count)
              ? response.data?.count.length || 0
              : response.data?.count || 0
          }
          dispatch(updateFieldValue('analyticsCurriculum', 'list', data))
          return { error: false, data }
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        return { error: true, message }
      } finally {
        dispatch(updateFieldValue('analyticsCurriculum', 'isLoadingList', false))
      }
    },
    [code, dispatch]
  )

  const deleteHistory = async (historyId) => {
    dispatch(updateFieldValue('analyticsCurriculum', 'historyLoading', true))
    try {
      const response = await deleteHistoryService(code, historyId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        enqueueSnackbar(`History deleted successfully`, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      getHistory()
    }
  }

  return {
    createModule,
    updateModule,
    deleteModule,
    getAnalyticsCurriculumCurrent,
    getAnalyticsCurriculumByStudent,
    getAnalyticsCurriculumByClasses,
    generateDataForAnalyticsCurriculum,
    getProgress,
    updateStatusDevelopmentalRubricBatch,
    getHistory,
    getClasses,
    getStudents,
    deleteHistory
  }
}

export default useAnalyticsCurriculum
